import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/sps/Navbar";
import { Footer } from "@/components/sps/Footer";
import { ServiceGallery } from "@/components/sps/ServiceGallery";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const IMG = {
  distillingBg:
    "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/dcbmliu4_FONDO%20BANNER%20DISTILLING.webp",
  bottlingBg:
    "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/j3mc9ako_FONDO%20BANNER%20BOTTLING.webp",
  ctaBg:
    "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/d2cij9ue_BANNER%20CONT%C3%81CTANOS.webp",
  distillingLockup:
    "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/3qr2vqt7_LOGO%20DISTILLING.webp",
  bottlingLockup:
    "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/j3z37mb0_LOGO%20BOTTLING.webp",
};

const DISTILLING_IMAGES = [
  "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/szpbrpv7_FOTO%20DESTILLING_1.webp",
  "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/pl3w42gu_FOTO%20DISTILLING_2.webp",
  "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/un57vkqs_FOTO%20DISTILLING_3.webp",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_spirits-production/artifacts/40zst7hr_FOTO%20DISTILLING_4.webp",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_spirits-production/artifacts/l85mpinl_FOTO%20DISTILLING_5.webp",
];

const BOTTLING_IMAGES = [
  "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/wzl8rq6s_FOTO%20BOTTLING_1.webp",
  "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/eck548cc_FOTO%20BOTTLING_2.webp",
  "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/1twsu909_FOTO%20BOTTLING_3.webp",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_spirits-production/artifacts/4qjsazae_FOTO%20BOTTLING_4.webp",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_spirits-production/artifacts/ery2rtgk_FOTO%20BOTTLING_5.webp",
];

const DISTILLING_PARAGRAPHS = [
  "Infraestructura propia para destilación, almacenamiento y envasado.",
  "Operación diseñada para adaptarse a proyectos de distintos tamaños y etapas de crecimiento.",
  "Amplia flexibilidad para desarrollar perfiles sensoriales personalizados de acuerdo con los objetivos de cada marca.",
  "En SPS Distilling combinamos la tradición tequilera con procesos eficientes y estrictos estándares de calidad para desarrollar destilados consistentes, escalables y alineados con la visión de nuestros clientes.",
];

const BOTTLING_PARAGRAPHS = [
  "Líneas de envasado de alta velocidad diseñadas para maximizar eficiencia y productividad.",
  "Operación continua capaz de atender proyectos de distintas escalas y complejidades.",
  "Infraestructura especializada que garantiza calidad, inocuidad, trazabilidad y eficiencia operativa en cada etapa del proceso.",
  "Desde corridas piloto hasta producciones industriales, SPS Bottling ofrece la infraestructura, el conocimiento técnico y la flexibilidad necesarios para desarrollar productos desde cero, acompañar el crecimiento de tu marca y responder a las demandas de mercados nacionales e internacionales.",
];

const ServiceHero = ({ bg, lockup, tagline, testid, logoClassName, logoStyle, taglineFontSize, containerStyle }) => (
  <section
    data-testid={testid}
    className="relative flex items-center overflow-hidden bg-[#001825]"
  >
    <div className="absolute inset-0 z-0">
      <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" />
    </div>
    <div
      className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-40 md:py-48 w-full"
      style={containerStyle}
    >
      <div className="max-w-2xl">
        <img
          src={lockup}
          alt=""
          className={
            logoClassName ||
            "h-14 md:h-16 w-auto object-contain mb-8 mix-blend-screen"
          }
          style={logoStyle}
        />
        <h1
          className="font-sans-modern font-medium text-white uppercase tracking-tight leading-[1.1]"
          style={{ fontSize: taglineFontSize || "clamp(1.75rem, 3.5vw, 3rem)" }}
        >
          {tagline}
        </h1>
      </div>
    </div>
  </section>
);

const ServiceBlock = ({ heading, headingBold, paragraphs, gallery, testid, withDividers, verticalAccent }) => (
  <section
    data-testid={testid}
    className={`relative bg-white py-16 md:py-20 lg:py-24 ${
      withDividers ? "border-t border-b border-[#c9ced3]" : ""
    }`}
  >
    <div className="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
        {/* LEFT — text */}
        <div className="lg:col-span-5 flex flex-col justify-center">
          <div
            className={`font-sans-modern text-[#002438] leading-[1.75rem] tracking-tight ${
              verticalAccent
                ? "pl-4 border-l-[3px] border-[#002438] mb-8"
                : "uppercase mb-3"
            }`}
            style={{
              fontSize: verticalAccent ? "1.125rem" : "3rem",
              fontWeight: verticalAccent ? 300 : 500,
            }}
          >
            {heading}
            {headingBold && <span className="ml-1">{headingBold}</span>}
          </div>
          {!verticalAccent && <div className="h-px w-12 bg-[#002438]/70 mb-8" />}
          <div
            className="space-y-5 text-[#002438] font-light font-sans-modern"
            style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
          >
            {paragraphs.map((p, i) => (
              <p
                key={i}
                className={
                  verticalAccent ? "pl-4 border-l-[3px] border-[#002438]" : ""
                }
              >
                {p}
              </p>
            ))}
          </div>
        </div>

        {/* RIGHT — gallery */}
        <div className="lg:col-span-7">{gallery}</div>
      </div>
    </div>
  </section>
);

const ImageStrip = ({ images, testid }) => {
  const n = images.length;
  const [center, setCenter] = useState(n >= 3 ? 1 : 0);

  const prev = () => setCenter((c) => (c - 1 + n) % n);
  const next = () => setCenter((c) => (c + 1) % n);

  const visible = n <= 3
    ? images.map((src, i) => ({ src, idx: i, position: i === Math.floor(n / 2) ? "center" : "side" }))
    : [
        { src: images[(center - 1 + n) % n], idx: (center - 1 + n) % n, position: "side" },
        { src: images[center], idx: center, position: "center" },
        { src: images[(center + 1) % n], idx: (center + 1) % n, position: "side" },
      ];

  return (
    <div data-testid={testid} className="relative flex items-stretch gap-2 md:gap-3 h-auto lg:h-full pt-4">
      <button
        type="button"
        aria-label="Anterior"
        data-testid={`${testid}-prev`}
        onClick={prev}
        className="hidden md:flex flex-shrink-0 h-9 w-9 self-center rounded-full text-[#002438]/70 items-center justify-center hover:text-[#002438] transition-colors"
      >
        <ChevronLeft size={22} strokeWidth={1.5} />
      </button>
      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 flex-1 items-center">
        {visible.map((v, i) => {
          const isCenter = v.position === "center";
          return (
            <div
              key={`${v.idx}-${i}`}
              className={`relative overflow-hidden rounded-2xl bg-[#eef1f4] transition-all duration-700 ease-out ${
                isCenter
                  ? "aspect-[3/4] md:aspect-[3/5] lg:aspect-auto lg:h-full md:scale-[1.04] z-10 shadow-[0_20px_50px_-20px_rgba(0,36,56,0.35)]"
                  : "hidden md:block aspect-[3/5] lg:aspect-auto lg:h-[80%] self-center scale-100 opacity-90"
              }`}
            >
              <img
                src={v.src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          );
        })}

        {/* Mobile-only overlay arrows */}
        <button
          type="button"
          aria-label="Anterior"
          data-testid={`${testid}-prev-mobile`}
          onClick={prev}
          className="md:hidden absolute left-3 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white/85 hover:bg-white text-[#002438] flex items-center justify-center backdrop-blur-sm shadow-[0_6px_20px_-6px_rgba(0,36,56,0.35)]"
        >
          <ChevronLeft size={20} strokeWidth={1.5} />
        </button>
        <button
          type="button"
          aria-label="Siguiente"
          data-testid={`${testid}-next-mobile`}
          onClick={next}
          className="md:hidden absolute right-3 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white/85 hover:bg-white text-[#002438] flex items-center justify-center backdrop-blur-sm shadow-[0_6px_20px_-6px_rgba(0,36,56,0.35)]"
        >
          <ChevronRight size={20} strokeWidth={1.5} />
        </button>

        {/* Mobile-only dots */}
        <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCenter(i)}
              data-testid={`${testid}-dot-${i}`}
              aria-label={`Ir a la imagen ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === center ? "w-6 bg-white" : "w-1.5 bg-white/70 hover:bg-white/90"
              }`}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        aria-label="Siguiente"
        data-testid={`${testid}-next`}
        onClick={next}
        className="hidden md:flex flex-shrink-0 h-9 w-9 self-center rounded-full text-[#002438]/70 items-center justify-center hover:text-[#002438] transition-colors"
      >
        <ChevronRight size={22} strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default function ServiciosPage() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id) || document.querySelector(`[data-testid="${id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location]);

  return (
    <div className="App min-h-screen bg-white text-[#002438] relative">
      <Navbar />
      <main className="relative">
        {/* DISTILLING HERO */}
        <ServiceHero
          testid="distilling-hero"
          bg={IMG.distillingBg}
          lockup={IMG.distillingLockup}
          tagline={
            <>
              Tradición tequilera respaldada
              <br />
              por infraestructura y experiencia.
            </>
          }
          logoClassName="w-auto object-contain mb-8"
          logoStyle={{ height: "15rem" }}
          taglineFontSize="1.5rem"
        />

        {/* DISTILLING DETAILS */}
        <ServiceBlock
          testid="distilling-details"
          heading="Certificación "
          headingBold="NOM 1662."
          paragraphs={DISTILLING_PARAGRAPHS}
          withDividers
          verticalAccent
          gallery={<ImageStrip images={DISTILLING_IMAGES} testid="distilling-gallery" />}
        />

        {/* BOTTLING HERO */}
        <ServiceHero
          testid="bottling-hero"
          bg={IMG.bottlingBg}
          lockup={IMG.bottlingLockup}
          tagline={
            <>
              Capacidad, velocidad y precisión para
              <br />
              impulsar el crecimiento de tu marca.
            </>
          }
          logoClassName="w-auto object-contain mb-8"
          logoStyle={{ height: "15rem" }}
          taglineFontSize="1.5rem"
          containerStyle={{ paddingTop: "7rem" }}
        />

        {/* BOTTLING DETAILS */}
        <ServiceBlock
          testid="bottling-details"
          heading="Amplia versatilidad "
          headingBold="de formatos."
          paragraphs={BOTTLING_PARAGRAPHS}
          withDividers
          verticalAccent
          gallery={<ImageStrip images={BOTTLING_IMAGES} testid="bottling-gallery" />}
        />

        {/* CTA */}
        <section
          data-testid="servicios-cta"
          className="relative overflow-hidden"
        >
          <div className="absolute inset-0 z-0">
            <img
              src={IMG.ctaBg}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-14 md:py-16 lg:py-20 flex flex-col items-center text-center gap-6">
            <h2
              data-testid="cta-title"
              className="font-sans-modern font-medium text-white uppercase leading-[1.15] tracking-tight"
              style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.75rem)" }}
            >
              ¿Buscas un socio para tu proyecto?
            </h2>
            <div className="h-px w-14 bg-white/70" />
            <button
              type="button"
              data-testid="cta-button"
              onClick={() => navigate("/contacto")}
              className="inline-flex items-center justify-center px-14 py-3.5 bg-[#001825]/40 border border-white text-white uppercase tracking-[0.25em] font-sans-modern hover:bg-white hover:text-[#001825] transition-colors duration-300"
              style={{ fontSize: "0.98rem" }}
            >
              Contáctanos
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
